// src/components/common/GlobalSearchBar.js
import React, { useRef, useEffect, useContext } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FaSearch, FaTimes } from 'react-icons/fa';
import { useTranslation } from '../../hooks/useTranslation';
import { ThemeContext } from '../../context/ThemeContext';

const SearchBarContainer = styled(motion.div)`
  position: fixed;
  top: 0;
  height: 60px;
  display: flex;
  align-items: center;
  padding: 0 1.5rem;
  z-index: 999;
  background: transparent;
  
  /* Desktop - between collapsed sidebar and navbar */
  @media (min-width: 1025px) {
    left: 70px; /* Collapsed sidebar width */
    right: 0;
  }
  
  /* Tablet - full width with padding for sidebar */
  @media (min-width: 769px) and (max-width: 1024px) {
    left: 0;
    right: 0;
    height: 60px;
    padding: 0 1.5rem;
    background: ${props => props.theme.name === 'light'
      ? 'rgba(255, 255, 255, 0.95)'
      : 'rgba(15, 15, 35, 0.95)'};
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    box-shadow: ${props => props.theme.name === 'light'
      ? '0 4px 20px rgba(0, 0, 0, 0.1)'
      : '0 4px 20px rgba(0, 0, 0, 0.3)'};
  }
  
  /* Mobile - Full width overlay */
  @media (max-width: 768px) {
    left: 0;
    right: 0;
    height: 60px;
    padding: 0 1rem;
    z-index: 1001;
    background: ${props => props.theme.name === 'light'
      ? 'rgba(255, 255, 255, 0.98)'
      : 'rgba(15, 15, 35, 0.98)'};
    backdrop-filter: blur(25px);
    -webkit-backdrop-filter: blur(25px);
    box-shadow: ${props => props.theme.name === 'light'
      ? '0 8px 32px rgba(0, 0, 0, 0.15)'
      : '0 8px 32px rgba(0, 0, 0, 0.4)'};
    border-radius: 0 0 20px 20px;
  }
`;

const SearchWrapper = styled(motion.div)`
  position: relative;
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    /* Mobile - Full width */
    max-width: 100%;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.5rem 1rem 0.5rem 2.5rem;
  border: 1px solid ${props => props.theme.name === 'light' 
    ? 'rgba(255, 255, 255, 0.3)' 
    : 'rgba(255, 255, 255, 0.2)'};
  border-radius: ${props => props.theme.borderRadius.full};
  font-size: 0.9rem;
  transition: all ${props => props.theme.transitions.short};
  background: ${props => props.theme.name === 'light' 
    ? 'rgba(255, 255, 255, 0.9)' 
    : 'rgba(255, 255, 255, 0.1)'};
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  color: ${props => props.theme.name === 'light' 
    ? '#333333' 
    : '#ffffff'};
  box-shadow: ${props => props.theme.name === 'light'
    ? '0 4px 16px rgba(0, 0, 0, 0.1)'
    : '0 4px 16px rgba(0, 0, 0, 0.3)'
  };
  font-family: ${props => props.theme.typography?.fontFamily || "'Inter', 'Poppins', 'Roboto', sans-serif"};
  
  &::placeholder {
    color: ${props => props.theme.name === 'light' 
      ? '#666666' 
      : '#cccccc'};
  }
  
  &:focus {
    outline: none;
    border: 1px solid ${props => props.theme.name === 'light'
      ? 'rgba(0, 0, 0, 0.3)'
      : 'rgba(255, 255, 255, 0.4)'
    };
    background: ${props => props.theme.name === 'light' 
      ? 'rgba(255, 255, 255, 1)' 
      : 'rgba(255, 255, 255, 0.15)'};
    box-shadow: ${props => props.theme.name === 'light'
      ? '0 4px 16px rgba(0, 0, 0, 0.15)'
      : '0 4px 16px rgba(0, 0, 0, 0.4)'
    };
  }
  
  @media (max-width: 768px) {
    /* Mobile - Larger, more prominent */
    padding: 0.75rem 3rem 0.75rem 2.75rem;
    font-size: 1rem;
    background: ${props => props.theme.name === 'light' 
      ? 'rgba(255, 255, 255, 1)' 
      : 'rgba(255, 255, 255, 0.15)'};
    border: 1px solid ${props => props.theme.name === 'light'
      ? 'rgba(0, 0, 0, 0.1)'
      : 'rgba(255, 255, 255, 0.2)'
    };
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: ${props => props.theme.colors.text.secondary};
  font-size: 0.9rem;
  pointer-events: none;
  z-index: 1;
`;

const CloseSearchButton = styled(motion.button)`
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: ${props => props.theme.colors.text.secondary};
  font-size: 1rem;
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all ${props => props.theme.transitions.short};
  z-index: 1;
  
  &:hover {
    background: ${props => props.theme.name === 'light' 
      ? 'rgba(0, 0, 0, 0.1)' 
      : 'rgba(255, 255, 255, 0.1)'};
    color: ${props => props.theme.colors.text.primary};
    transform: translateY(-50%) scale(1.1);
  }
  
  @media (max-width: 768px) {
    /* Mobile - Larger, easier to tap */
    right: 1rem;
    padding: 0.5rem;
    font-size: 1.125rem;
  }
`;

const searchContainerVariants = {
  hidden: {
    opacity: 0,
    x: '-100%',
    transition: {
      duration: 0.4,
      ease: [0.4, 0, 0.2, 1]
    }
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.5,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};

const searchWrapperVariants = {
  hidden: {
    opacity: 0,
    scale: 0.9,
    transition: {
      duration: 0.2
    }
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.4,
      delay: 0.15,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};

const GlobalSearchBar = ({ isVisible, onClose, sidebarWidth, searchQuery, setSearchQuery }) => {
  const searchInputRef = useRef(null);
  const { theme } = useContext(ThemeContext);
  const { t } = useTranslation();

  // Focus search input when it becomes visible
  useEffect(() => {
    if (isVisible && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isVisible]);

  // Handle escape key to close search
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isVisible) {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isVisible, onClose]);

  return (
    <AnimatePresence>
      {isVisible && (
        <SearchBarContainer
          initial="hidden"
          animate="visible"
          exit="hidden"
          variants={searchContainerVariants}
          sidebarWidth={sidebarWidth}
          theme={theme}
        >
          <SearchWrapper
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={searchWrapperVariants}
          >
            <SearchIcon theme={theme}>
              <FaSearch />
            </SearchIcon>
            <SearchInput
              ref={searchInputRef}
              type="text"
              placeholder={t('common.search')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              theme={theme}
            />
            <CloseSearchButton
              onClick={() => {
                onClose();
                setSearchQuery('');
              }}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              title="Close search"
              theme={theme}
            >
              <FaTimes />
            </CloseSearchButton>
          </SearchWrapper>
        </SearchBarContainer>
      )}
    </AnimatePresence>
  );
};

export default GlobalSearchBar;

